/**
 * The FAQs for `/tools/mastodon-best-time`, in their own module.
 *
 * A route's `head` is never code-split, so importing this list from the tool
 * component would pull the whole component into the shared entry chunk that
 * every visitor to every page downloads — the trap `marketing-perf-ceiling`
 * documents, and one the Mastodon limit checker was caught by.
 *
 * Rendered visibly on the page AND emitted as `FAQPage` JSON-LD from the route.
 * Marking up a question that is not on the page is a structured-data violation,
 * so both read from this one list.
 */
export const MASTODON_TIMING_FAQS = [
  {
    q: "What is the best time to post on Mastodon?",
    a: "There isn't one, and any chart that gives you a single answer is guessing. Your reach on Mastodon depends on who follows you, which servers they are on and what your instance federates with — none of which a generic table knows. The only data that can answer it for you is your own posting history, which is what this page reads.",
  },
  {
    q: "Does this need my Mastodon password?",
    a: "No, and it never will. Everything comes from your server's public API, which needs no authentication, and your own browser makes the request rather than our servers. There is no login, no OAuth and no app password. Nothing about your account is stored.",
  },
  {
    q: "Why does it say there isn't enough data?",
    a: "Because saying so is more useful than a confident chart built on ten posts. A slot is only called a signal when its confidence interval clears your own average, and with a small sample almost nothing does. That is a real finding about your data, not a limitation of the tool — most accounts genuinely do not post often enough to detect a timing effect.",
  },
  {
    q: "Why are boosts and replies excluded?",
    a: "A boost carries someone else's post, so its timing tells you when they posted, not when you should. Replies reach a conversation rather than your followers and get engagement on entirely different terms. Including either would answer a different question from the one you asked.",
  },
  {
    q: "Does the time of day matter as much on Mastodon as on other networks?",
    a: "Probably less, though nobody has measured it properly. Mastodon's default timeline is chronological, so a post is mostly seen by followers who are online near when you post it — which makes timing matter in a simple, direct way. There is no algorithm deciding to resurface an old post later, and no engagement-based ranking to game. What that means for any one account is exactly what this page measures.",
  },
] as const;
